import {
  isTenantAssetContentType,
  MAX_TENANT_ASSET_BYTES,
  type TenantAssetContentType,
  type TenantAssetKind,
} from "@loot-os/api-contract";

export const TENANT_ASSET_FIELDS = {
  logo: "logoUrl",
  favicon: "faviconUrl",
  banner: "bannerUrl",
  badge: "badgeUrl",
} as const satisfies Record<TenantAssetKind, "logoUrl" | "faviconUrl" | "bannerUrl" | "badgeUrl">;

export type TenantAssetUrlField = (typeof TENANT_ASSET_FIELDS)[TenantAssetKind];

/** What the server's upload start answers: the key to finish with, and where to PUT the file. */
export interface TenantAssetUploadTicket {
  uploadKey: string;
  url: string;
  headers: Record<string, string>;
}

/** A picked file's type and size, which the upload URL is signed for. */
export interface TenantAssetUploadStart {
  contentType: TenantAssetContentType;
  byteLength: number;
}

/**
 * Uploads one picked file: `start` signs a PUT for its type and size, the browser sends the bytes
 * straight to the bucket (never through the dashboard API, whose Lambda caps a request at 6 MB),
 * and `finish` has the server check what landed. SVG and oversized files are refused here so the
 * picker fails immediately; the server checks both again.
 */
export async function uploadTenantAssetFile<T>(
  file: File,
  start: (input: TenantAssetUploadStart) => Promise<TenantAssetUploadTicket>,
  finish: (uploadKey: string) => Promise<T>,
): Promise<T> {
  const contentType = requireTenantAssetContentType(file);
  if (file.size > MAX_TENANT_ASSET_BYTES) {
    throw new Error(
      `the file is larger than ${Math.floor(MAX_TENANT_ASSET_BYTES / 1024 / 1024)} MB`,
    );
  }
  const ticket = await start({ contentType, byteLength: file.size });
  const put = await fetch(ticket.url, { method: "PUT", headers: ticket.headers, body: file });
  if (!put.ok) throw new Error(`the upload was refused (${put.status})`);
  return finish(ticket.uploadKey);
}

const CONTENT_TYPE_BY_EXTENSION: Record<string, TenantAssetContentType> = {
  png: "image/png",
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  webp: "image/webp",
  avif: "image/avif",
};

/** The file's type as the browser reports it, else from its extension (some report AVIF as ""). */
function requireTenantAssetContentType(file: File): TenantAssetContentType {
  if (isTenantAssetContentType(file.type)) return file.type;
  const extension = /\.([a-z0-9]+)$/i.exec(file.name)?.[1]?.toLowerCase();
  const fromName = extension ? CONTENT_TYPE_BY_EXTENSION[extension] : undefined;
  if (file.type === "" && fromName) return fromName;
  throw new Error("only PNG, JPEG, WEBP and AVIF images are allowed");
}
